import React from 'react';
import { Box, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { animalOf, AXES, Cut, CUT_ITEMS, CutItem, eatenAsMeat, findCuts, ratingRanges, Region, regionsOfWord } from '../../utils/guide';
import { ScanLine } from './scanTypes';
import classes from './YakinikuView.module.css';

/**
 * A menu line that names beef, and everything it names (a platter can list
 * several cuts). `horumon` lines are offal, listed apart from the meat.
 */
export interface BeefLine {
  index: number;
  line: ScanLine;
  items: CutItem[];
  horumon: boolean;
}

// タン, ハラミ and サガリ are offal on paper but meat at yakiniku: they go on the chart
const isOffal = (it: CutItem) => (it.kind === 'offal' && !eatenAsMeat(it)) || (it.kind === 'word' && it.id === 'horumon');

/**
 * The lines that name beef: meat (a cut, a section, a menu word like カルビ) or
 * offal (ホルモン). A line (or a whole menu) about pork or chicken isn't beef.
 * A menu word next to offal describes the offal: タンカルビ is tongue.
 */
export function beefLines(lines: ScanLine[], menuAnimal: string | null): BeefLine[] {
  const out: BeefLine[] = [];
  lines.forEach((line, index) => {
    if (line.dropped) return;
    const animal = animalOf(line.t);
    if (animal ? animal !== 'beef' : menuAnimal) return;
    const named = [...new Set(findCuts(line.t).flatMap((h) => h.keys))].map((k) => CUT_ITEMS.get(k)!);
    if (!named.length) return;
    // A menu word next to an organ describes it, whether it's eaten as meat or not: タンカルビ is tongue
    const organs = named.filter((it) => it.kind === 'offal');
    const items = organs.length && named.every((it) => it.kind === 'offal' || it.kind === 'word') ? organs : named;
    out.push({ index, line, items, horumon: items.every(isOffal) });
  });
  return out;
}

/** The order the view lists lines in: the meat, then the offal. */
export const yakinikuOrder = (items: BeefLine[]) => [...items.filter((b) => !b.horumon), ...items.filter((b) => b.horumon)].map((b) => b.index);

const TENDER = 0, MARBLING = 1;

/** Where a meat name sits on the chart. A cut has its own ratings; a section or a menu word like カルビ is an area, placed at its cuts' average, with each rating's range. */
interface Rated { at: [number, number]; ranges: [number, number][]; area: boolean }

const regionsOf = (it: CutItem) => (it.kind === 'region' ? [it.n] : it.kind === 'word' ? regionsOfWord(it) : []);

function rate(it: CutItem): Rated | null {
  if (it.kind === 'cut' || eatenAsMeat(it)) return { at: [it.s[TENDER], it.s[MARBLING]], ranges: it.s.map((n) => [n, n] as [number, number]), area: false };
  const regions = regionsOf(it);
  const { ranges } = ratingRanges(regions);
  if (!ranges.length) return null;
  const cuts = [...CUT_ITEMS.values()].filter((c): c is Cut => c.kind === 'cut' && regions.includes(c.r));
  const avg = (k: number) => cuts.reduce((a, c) => a + c.s[k], 0) / cuts.length;
  return { at: [avg(TENDER), avg(MARBLING)], ranges, area: true };
}

// Chart geometry, in viewBox units. Tenderness is rated 1–5, marbling 0–5; the
// marbling scale runs past both ends so the quadrant names clear the dots.
const W = 340, H = 320;
const M = { l: 30, r: 10, t: 10, b: 32 };
const X0 = 0.5, X1 = 5.5, Y0 = -1.2, Y1 = 6.2;
const sx = (v: number) => M.l + ((v - X0) / (X1 - X0)) * (W - M.l - M.r);
const sy = (v: number) => H - M.b - ((v - Y0) / (Y1 - Y0)) * (H - M.t - M.b);
const DOT = 10;

/** Dots that share a spot sit in a little ring around it instead of on top of each other. */
function offsets(n: number): [number, number][] {
  if (n === 1) return [[0, 0]];
  const r = n === 2 ? DOT + 1 : DOT + 4;
  return Array.from({ length: n }, (_, k) => {
    const a = -Math.PI / 2 + (2 * Math.PI * k) / n;
    return [r * Math.cos(a), r * Math.sin(a)];
  });
}

/** Filled up to lo; lightly up to hi, when it depends on the cut. */
const Pips: React.FC<{ range: [number, number]; label: string }> = ({ range: [lo, hi], label }) => (
  <span className={classes.pips} role="img" aria-label={lo === hi ? `${label} ${lo} out of 5` : `${label} ${lo} to ${hi} out of 5, depending on the cut`}>
    {[1, 2, 3, 4, 5].map((p) => <span key={p} className={`${classes.pip} ${p <= lo ? classes.pipOn : p <= hi ? classes.pipMaybe : ''}`} />)}
  </span>
);

interface Point extends Rated { item: CutItem; n: number; index: number }

interface YakinikuViewProps {
  items: BeefLine[];
  selected: number;
  onPick: (index: number) => void;
}

/**
 * The yakiniku view of a scanned menu: just the beef, in two parts.
 * Meat: every name on a tenderness × marbling chart, numbered the same in the
 * list below. A cut is a solid dot; a section or a word like カルビ is a hollow
 * one at its cuts' average. Tapping a dot opens the first line that names it.
 * Horumon: the offal, listed (the guide doesn't rate it).
 */
export const YakinikuView: React.FC<YakinikuViewProps> = ({ items, selected, onPick }) => {
  const meat = items.filter((b) => !b.horumon);
  const horumon = items.filter((b) => b.horumon);

  // Each name once, numbered in reading order, with the first line that names it
  const points = new Map<string, Point>();
  meat.forEach((b) => b.items.forEach((it) => {
    if (points.has(it.key)) return;
    const r = rate(it);
    if (r) points.set(it.key, { ...r, item: it, n: points.size + 1, index: b.index });
  }));
  const lit = new Set(meat.find((b) => b.index === selected)?.items.map((it) => it.key));
  const areas = [...points.values()].some((p) => p.area);

  // Dots that land on the same spot fan out
  const spots = new Map<string, Point[]>();
  points.forEach((p) => {
    const key = `${p.at[0].toFixed(1)},${p.at[1].toFixed(1)}`;
    spots.set(key, [...(spots.get(key) || []), p]);
  });
  const dots = [...spots.values()].flatMap((group) => {
    const off = offsets(group.length);
    return group.map((p, k) => ({ p, x: sx(p.at[0]) + off[k][0], y: sy(p.at[1]) + off[k][1] }));
  });
  // Areas under cuts, and the selected line's dots on top
  dots.sort((a, b) => Number(lit.has(a.p.item.key)) - Number(lit.has(b.p.item.key)) || Number(b.p.area) - Number(a.p.area));

  const midX = sx(3), midY = sy(2.5);
  const [tenderEn, tenderJa] = AXES[TENDER];
  const [marblingEn, marblingJa] = AXES[MARBLING];

  return (
    <Stack gap="md" className={classes.view}>
      {meat.length > 0 && (
        <Box>
          <Text fw={700} size="lg">Meat</Text>
          <Text size="sm" c="dimmed">
            By how tender and how marbled it is. {areas ? 'Hollow dots are names like カルビ that cover several cuts, placed at their average. ' : ''}Tap one to see what it is.
          </Text>
        </Box>
      )}

      {points.size > 0 && (
        <svg className={classes.chart} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`The meat by ${tenderEn.toLowerCase()} and ${marblingEn.toLowerCase()}; the list below has the same numbers`}>
          {/* Quadrants */}
          <rect x={M.l} y={M.t} width={W - M.l - M.r} height={H - M.t - M.b} className={classes.plot} />
          <line x1={midX} x2={midX} y1={M.t} y2={H - M.b} className={classes.mid} />
          <line x1={M.l} x2={W - M.r} y1={midY} y2={midY} className={classes.mid} />
          <text x={W - M.r - 6} y={M.t + 14} textAnchor="end" className={classes.quad}>Tender &amp; marbled</text>
          <text x={M.l + 6} y={M.t + 14} className={classes.quad}>Firm &amp; marbled</text>
          <text x={W - M.r - 6} y={H - M.b - 8} textAnchor="end" className={classes.quad}>Tender &amp; lean</text>
          <text x={M.l + 6} y={H - M.b - 8} className={classes.quad}>Firm &amp; lean</text>

          {/* Axes: ticks at each rating, names along the edges */}
          {[1, 2, 3, 4, 5].map((v) => <text key={`x${v}`} x={sx(v)} y={H - M.b + 13} textAnchor="middle" className={classes.tick}>{v}</text>)}
          {[0, 1, 2, 3, 4, 5].map((v) => <text key={`y${v}`} x={M.l - 6} y={sy(v)} textAnchor="end" dominantBaseline="central" className={classes.tick}>{v}</text>)}
          <text x={(M.l + W - M.r) / 2} y={H - 3} textAnchor="middle" className={classes.axis}>{tenderEn} <tspan lang="ja">{tenderJa}</tspan> →</text>
          <text transform={`translate(10 ${(M.t + H - M.b) / 2}) rotate(-90)`} textAnchor="middle" className={classes.axis}>{marblingEn} <tspan lang="ja">{marblingJa}</tspan> →</text>

          {dots.map(({ p, x, y }) => (
            <g
              key={p.item.key}
              className={`${classes.dot} ${p.area ? classes.dotArea : ''} ${lit.has(p.item.key) ? classes.dotOn : ''}`}
              onClick={() => onPick(p.index)}
              role="button"
              tabIndex={0}
              aria-label={`${p.n}. ${p.item.en || p.item.ro}${p.area ? ', several cuts' : ''}`}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(p.index); } }}
            >
              <title>{`${p.item.ja} · ${p.item.en || p.item.ro}: ${tenderEn.toLowerCase()} ${[...new Set(p.ranges[TENDER])].join('–')}, ${marblingEn.toLowerCase()} ${[...new Set(p.ranges[MARBLING])].join('–')}`}</title>
              {/* A bigger, invisible circle so small dots are easy to tap */}
              <circle cx={x} cy={y} r={DOT + 6} className={classes.hit} />
              <circle cx={x} cy={y} r={DOT} className={classes.dotMark} />
              <text x={x} y={y} textAnchor="middle" dominantBaseline="central" className={classes.dotText}>{p.n}</text>
            </g>
          ))}
        </svg>
      )}

      {meat.length > 0 && <Rows items={meat} selected={selected} onPick={onPick} points={points} />}
      {meat.length > 0 && (
        <Text size="xs" c="dimmed">
          Ratings: TOMMY BEEF, for Shiroishi beef; タン, ハラミ and サガリ are our estimate, as TOMMY BEEF doesn’t rate offal. A restaurant’s own beef can differ.
        </Text>
      )}

      {horumon.length > 0 && (
        <Box mt={meat.length ? 'lg' : 0}>
          <Text fw={700} size="lg">Horumon <span lang="ja">ホルモン</span></Text>
          <Text size="sm" c="dimmed">Offal: stomachs, intestines and the rest. The guide doesn’t rate it for tenderness or marbling.</Text>
        </Box>
      )}
      {horumon.length > 0 && <Rows items={horumon} selected={selected} onPick={onPick} />}
    </Stack>
  );
};

interface RowsProps {
  items: BeefLine[];
  selected: number;
  onPick: (index: number) => void;
  /** The chart's points, for the meat; offal has none */
  points?: Map<string, Point>;
}

/** A row per line, and in it a row per thing the line names. */
const Rows: React.FC<RowsProps> = ({ items, selected, onPick, points }) => {
  const [tenderEn] = AXES[TENDER];
  const [marblingEn] = AXES[MARBLING];
  return (
    <ul className={classes.rows}>
      {items.map((b) => (
        <li key={b.index}>
          <UnstyledButton className={classes.row} onClick={() => onPick(b.index)} aria-current={b.index === selected}>
            <span className={classes.rowMain}>
              <Text component="span" fw={600} lang="ja" className={classes.printed}>{b.line.t}</Text>
              {b.items.map((it) => {
                const p = points?.get(it.key);
                return (
                  <span key={it.key} className={classes.named}>
                    {points && <span className={`${classes.num} ${p?.area ? classes.numArea : ''} ${p ? '' : classes.numNone}`} aria-hidden="true">{p?.n ?? '–'}</span>}
                    <span className={classes.namedMain}>
                      <Text component="span" size="xs" c="dimmed">
                        <span lang="ja">{it.ja}</span> · {it.ro}{it.en ? ` · ${it.en}` : ''}
                      </Text>
                      {p && (
                        <span className={classes.ratings}>
                          <span><Text component="span" size="xs" c="dimmed" w={64} display="inline-block">Tender</Text><Pips range={p.ranges[TENDER]} label={tenderEn} /></span>
                          <span><Text component="span" size="xs" c="dimmed" w={64} display="inline-block">Marbling</Text><Pips range={p.ranges[MARBLING]} label={marblingEn} /></span>
                        </span>
                      )}
                      {p?.area && (
                        <Text component="span" size="xs" c="dimmed" fs="italic">
                          {it.kind === 'region' ? 'A whole section of the cow' : 'Can be several cuts'}, from{' '}
                          <span lang="ja">{regionsOf(it).map((n) => (CUT_ITEMS.get(`r${n}`) as Region).ja).join('・')}</span>; light dots depend on the cut
                        </Text>
                      )}
                      {it.kind === 'offal' && (
                        <Text component="span" size="xs" c="dimmed" fs="italic">
                          {eatenAsMeat(it) ? 'Classed as offal, eaten as meat' : <>{it.group?.en} · <span lang="ja">{it.group?.ja}</span></>}
                        </Text>
                      )}
                      {it.kind === 'word' && it.id === 'horumon' && <Text component="span" size="xs" c="dimmed" fs="italic">Offal in general</Text>}
                    </span>
                  </span>
                );
              })}
            </span>
            <IconChevronRight size={16} className={classes.chev} />
          </UnstyledButton>
        </li>
      ))}
    </ul>
  );
};
